import React, { useState, useEffect } from 'react';
import { UserCheck } from 'lucide-react';
import AuthService from '../services/AuthService';
import { apiConfig } from '../config/api';
import AdminHeader from './shared/AdminHeader';
import axios from 'axios';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  approvalStatus: string;
  createdAt: string;
}

const UserApproval: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<{[key: string]: string}>({});

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const token = AuthService.getToken();
      if (!token) {
        setError('Authentication token not found. Please log in.');
        return;
      }

      const response = await fetch(`${apiConfig.baseURL}/api/admin/approval/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pending users');
      }

      let pendingUsersData = await response.json();

      // For single-tenant operation, all users are accessible globally
      // No need for school-based filtering

      setPendingUsers(pendingUsersData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching pending users:', err);
      if (err.response && err.response.status === 403) {
        setError('You don\'t have permission to access user approvals. Please contact an administrator.');
      } else {
        setError('Failed to load pending user approvals. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const handleApprove = async (userId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiConfig.baseURL}/api/admin/approval/process`,
        { userId, action: 'approve' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the list after approval
      fetchPendingUsers();
    } catch (err) {
      setError('Failed to approve user. Please try again.');
      console.error('Error approving user:', err);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      const reason = rejectionReason[userId] || 'No reason provided';
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiConfig.baseURL}/api/admin/approval/process`,
        { userId, action: 'reject', reason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowModal(null);
      // Refresh the list after rejection
      fetchPendingUsers();
    } catch (err) {
      setError('Failed to reject user. Please try again.');
      console.error('Error rejecting user:', err);
    }
  };

  const openRejectModal = (userId: string) => {
    setShowModal(userId);
  };

  if (loading) return <div className="text-center py-10">Loading pending approvals...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        <AdminHeader
          title="User Approval Management"
          icon={<UserCheck size={32} />}
          subtitle="Review and approve pending user registrations"
        />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {pendingUsers.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center py-8">
              <div className="text-gray-500 text-lg">No pending user approvals</div>
              <p className="text-gray-400 mt-2">All user registrations have been processed.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registration Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">@{user.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(user.id)}
                          className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openRejectModal(user.id)}
                          className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rejection Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Reject User Registration
                </h3>
                <textarea
                  className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:border-blue-500"
                  rows={4}
                  placeholder="Reason for rejection (optional)"
                  value={rejectionReason[showModal] || ''}
                  onChange={(e) => setRejectionReason(prev => ({
                    ...prev,
                    [showModal]: e.target.value
                  }))}
                />
                <div className="flex justify-end mt-4 space-x-2">
                  <button
                    onClick={() => setShowModal(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleReject(showModal)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-700"
                  >
                    Reject User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApproval;
